<template>
  <div class="search-destination">
    <Input
      id="destinationStation"
      :label="$t('travelPlanner.search.destination')"
      :internalLabel="true"
      v-model="inputValue"
      :placeholder="$t('travelPlanner.search.destination')"
      :icon="marker"
      :hasDelete="true"
      :suggestions="filteredSuggestions"
      @focus="fetchDestinationSuggestions"
      @select-suggestion="handleSelectSuggestion" />
  </div>
</template>

<script lang="ts">
  import { defineComponent, ref, computed, watch } from 'vue'
  import { useJourneyStore } from '@/stores/journeyStore'
  import { useMyTravelStore } from '@/stores/myTravelStore'
  import { getSortedStationSuggestions, SortableStation } from '@/utils/stationSortingUtils'
  import Input from '@/components/BaseComponents/Input.vue'
  import marker from '@/assets/icons/marker.svg'
  import { Station } from '@/types'

  type Suggestion = {
    uicCode: string
    name: string
  }

  export default defineComponent({
    name: 'SearchDestination',
    components: {
      Input,
    },
    setup() {
      const journeyStore = useJourneyStore()
      const myTravelStore = useMyTravelStore()
      const inputValue = ref(journeyStore.destinationStation.name || '')
      const showSuggestions = ref(false)
      const selectedUICCode = ref('')

      const fetchDestinationSuggestions = () => {
        // Only enable suggestions if we have an origin station
        if (journeyStore.OriginStation._u_i_c_station_code) {
          showSuggestions.value = true
        } else {
          // Show a message or handle the case where origin isn't selected
          inputValue.value = ''
        }
      }

      const handleSelectSuggestion = (code: string) => {
        selectedUICCode.value = code
        showSuggestions.value = false
        const selectedSuggestion = journeyStore.mappedDestinationSuggestions.find(
          (suggestion) => suggestion.uicCode === code,
        )
        if (selectedSuggestion) {
          journeyStore.setDestinationStation({
            name: selectedSuggestion.name,
            _u_i_c_station_code: selectedSuggestion.uicCode,
          })
          myTravelStore.setOriginDestination(myTravelStore.origin, selectedSuggestion.name)
          inputValue.value = selectedSuggestion.name
        }
      }

      const onSuggestionKeydown = (suggestion: Suggestion, event: KeyboardEvent) => {
        if (event.key === 'Enter') {
          handleSelectSuggestion(suggestion.uicCode)
        }
      }

      const filteredSuggestions = computed(() => {
        if (!inputValue.value) return []
        const stationsFromSuggestions: SortableStation[] = journeyStore.mappedDestinationSuggestions
          .filter((suggestion) =>
            suggestion.name.toLowerCase().includes(inputValue.value.toLowerCase()),
          )
          .map((suggestion) => ({
            name: suggestion.name,
            _u_i_c_station_code: suggestion.uicCode,
            sequence_number: suggestion.sequence_number,
            district: suggestion.district,
            county: suggestion.county,
          }))

        return getSortedStationSuggestions(stationsFromSuggestions, inputValue.value)
      })

      watch(inputValue, (newVal) => {
        if (!newVal) {
          showSuggestions.value = false
          myTravelStore.setOriginDestination(myTravelStore.origin, '')
        } else {
          showSuggestions.value = true
        }
      })

      watch(
        () => journeyStore.destinationStation,
        (newDestinationStation) => {
          if (newDestinationStation) {
            inputValue.value = newDestinationStation.name
            myTravelStore.setOriginDestination(myTravelStore.origin, newDestinationStation.name)
          }
        },
        { immediate: true },
      )

      return {
        inputValue,
        showSuggestions,
        fetchDestinationSuggestions,
        handleSelectSuggestion,
        marker,
        onSuggestionKeydown,
        filteredSuggestions,
        selectedUICCode,
      }
    },
  })
</script>
